<template>
    <section>
        <!-- Header -->
        <div class="flex flex-row justify-between">
            <h2>Agendas</h2>

            <div class="flex flex-row gap-2">
                <div>
                    <Select v-model="calendarOption" :options="calendarOptions" optionLabel="name" optionValue="value"
                        placeholder="Selecione" class="w-full md:w-56" />
                </div>

                <div>
                    <Button @click="openNewReservationModal" label="Novo" icon="pi pi-plus" />
                </div>
            </div>
        </div>

        <!-- Content -->
        <div class="my-6">
            <!-- Month: schedule-x.dev -->
            <!-- TODO: Ajustar reatividade desse componente -->
            <CalendarMonth v-if="calendarOption == 'month'" :calendarEvents="calendarEvents"
                @update:calendarRange="monthCalendarRange = $event"
                @day-dblclick="onMonthDayDblClick" />

            <!-- Week or day: custom -->
            <Calendar v-else :calendarMode="calendarOption" :calendarEvents="calendarEvents" :userData="userData"
                :controlDate="customCalendarControlDate" :controlHq="customCalendarHqFilter"
                :blockedDates="blockedDatesMeta"
                ref="weekDayCalendar"
                @update:selectedHq="customCalendarHqFilter = $event"
                @update:selectedRooms="customCalendarRoomFilter = $event"
                @update:calendarDate="customCalendarControlDate = $event" />
        </div>
        <DialogBlockDay
            v-if="blockDayDialogVisible"
            :visible="blockDayDialogVisible"
            :date="blockDaySelectedDate"
            :is-blocked="blockDayIsBlocked"
            :blocked-reason="blockDayReason"
            @close="blockDayDialogVisible = false"
            @confirm="confirmBlockDay"
        />
    </section>
</template>

<script>
import { Button, Select } from 'primevue';
import { addDays, format, parse, startOfMonth, endOfMonth, parseISO } from 'date-fns';
import Calendar from '@/components/Calendar.vue';
import CalendarMonth from '@/components/CalendarMonth.vue';
import DialogBlockDay from '@/components/DialogBlockDay.vue';

import ReservationService from '@/services/reservationService';
import UsersService from '@/services/usersService';
import dateService from '@/services/dateService';

export default {
    name: 'CalendarDisplay',
    components: {
        Select,
        Button,
        CalendarMonth,
        Calendar,
        DialogBlockDay
    },
    data() {
        return {
            isStaff: this.$store.state.accessLevel == 'admin' || this.$store.state.accessLevel == 'secretary',

            calendarOption: 'week',
            calendarOptions: [
                { name: 'Mês', value: 'month' },
                { name: 'Semana', value: 'week' },
                { name: 'Dia', value: 'day' }
            ],

            monthCalendarRange: null,
            userData: null,

            customCalendarControlDate: new Date(),
            customCalendarHqFilter: null,
            customCalendarRoomFilter: null,

            calendarEvents: [],
            blockedDatesMeta: [],

            // Block day dialog state
            blockDayDialogVisible: false,
            blockDaySelectedDate: null,
            blockDayIsBlocked: false,
            blockDayReason: null,
        }
    },
    methods: {
        openNewReservationModal() {
            const openModal = () => {
                const calendarRef = this.$refs.weekDayCalendar;
                if (calendarRef?.openCreateLocationModal) {
                    calendarRef.openCreateLocationModal();
                }
            };

            // Calendar.vue only exists when not in month mode.
            if (this.calendarOption === 'month') {
                this.calendarOption = 'week';
                this.$nextTick(() => openModal());
                return;
            }

            openModal();
        },
        getUrlPrefix() {
            return this.isStaff ? '/admin' : '/user';
        },
        onMonthDayDblClick(dateStr) {
            if (!this.isStaff) return;
            
            // Check if this date is already blocked
            const blockedEvent = this.calendarEvents.find(
                event => event.isBlocked && event.date === format(parseISO(dateStr), 'dd/MM/yyyy')
            );
            
            this.blockDaySelectedDate = dateStr;
            this.blockDayIsBlocked = !!blockedEvent;
            this.blockDayReason = blockedEvent ? blockedEvent.title.replace('🔒 Bloqueado: ', '') : null;
            this.blockDayDialogVisible = true;
        },
        confirmBlockDay({ date, reason, isUnblocking }) {
            if (!this.isStaff) return;
            const formattedDate = format(parseISO(date), 'yyyy-MM-dd');
            
            if (isUnblocking) {
                // Unblock the date
                dateService.unblockDate({ date: formattedDate })
                    .then(() => {
                        this.sendToast('Sucesso', `Dia ${format(parseISO(date), 'dd/MM/yyyy')} foi desbloqueado com sucesso.`, 'success');
                        this.blockDayDialogVisible = false;
                        this.fetchEvents();
                    })
                    .catch(error => {
                        console.error('(confirmBlockDay unblock)', error);
                        this.sendToast('Erro', 'Não foi possível desbloquear o dia.', 'error');
                    });
            } else {
                // Block the date
                dateService.blockDate({ date: formattedDate, reason: reason })
                    .then(() => {
                        this.sendToast('Sucesso', `Dia ${format(parseISO(date), 'dd/MM/yyyy')} foi bloqueado com sucesso.`, 'success');
                        this.blockDayDialogVisible = false;
                        this.fetchBlockedDates();
                    })
                    .catch(error => {
                        console.error('(confirmBlockDay block)', error);
                        this.sendToast('Erro', 'Não foi possível bloquear o dia.', 'error');
                    });
            }
        },
        getEventRequestFilters() {
            let startDate, endDate;

            if (this.calendarOption === 'month') {
                if (this.monthCalendarRange == null) {
                    startDate = startOfMonth(this.customCalendarControlDate);
                    endDate = endOfMonth(this.customCalendarControlDate);
                } else {
                    startDate = this.monthCalendarRange.start;
                    endDate = this.monthCalendarRange.end;
                }
            } else {
                startDate = addDays(this.customCalendarControlDate, -14);
                endDate = addDays(this.customCalendarControlDate, 14);
            }

            return {
                dtStart: format(startDate, 'yyyy-MM-dd'),
                dtEnd: format(endDate, 'yyyy-MM-dd'),
                headquartersId: this.customCalendarHqFilter,
                roomId: this.customCalendarRoomFilter,
            }
        },
        fetchEvents() {
            const filters = this.getEventRequestFilters();
            ReservationService.getEvents(filters)
                .then(response => {
                    const reservationEventsData = response.data;

                    // Start with reservation events, then add blocked dates
                    this.calendarEvents = reservationEventsData.map(eventData => {
                        const parsedDate = parse(eventData.date, 'yyyy-MM-dd', new Date());

                        return {
                            id: eventData.id,
                            date: format(parsedDate, 'dd/MM/yyyy'),
                            hour: `${String(eventData.hour).padStart(2, '0')}:00`,
                            roomId: eventData.roomInformations.id,
                            author: eventData.author.name,
                        }
                    });

                    // Fetch blocked dates after fetching regular events
                    this.fetchBlockedDates();
                })
                .catch(error => {
                    console.error('(fetchEvents)', error);
                    this.sendToast("Erro interno", "Algo deu errado. Tente novamente ou contate o suporte.", "error");
                    throw error;
                });
        },
        fetchBlockedDates() {
            const startDate = this.calendarOption === 'month'
                ? (this.monthCalendarRange?.start || startOfMonth(this.customCalendarControlDate))
                : addDays(this.customCalendarControlDate, -14);
            const endDate = this.calendarOption === 'month'
                ? (this.monthCalendarRange?.end || endOfMonth(this.customCalendarControlDate))
                : addDays(this.customCalendarControlDate, 14);

            const params = {
                startDate: format(startDate, 'yyyy-MM-dd'),
                endDate: format(endDate, 'yyyy-MM-dd'),
                isActive: true,
            };            
            if (this.customCalendarHqFilter) {
                params.headquartersId = this.customCalendarHqFilter;
            }

            dateService.getBlockedDate(params)
                .then(response => {
                    // API response structure: { data: [...], message, count, success }
                    const blockedDatesData = response.data.data;
                    
                    if (!Array.isArray(blockedDatesData)) {
                        console.warn('fetchBlockedDates: blockedDatesData is not an array', blockedDatesData);
                        return;
                    }

                    const blockedDateEvents = blockedDatesData.map(blockedDate => {
                        const parsedDate = parse(blockedDate.date, 'yyyy-MM-dd', new Date());
                        const hqIds = Array.isArray(blockedDate.headquarters)
                            ? blockedDate.headquarters.map(hq => hq.id)
                            : null;

                        return {
                            id: `blocked-${blockedDate.date}`,
                            title: `🔒 Bloqueado: ${blockedDate.reason}`,
                            date: format(parsedDate, 'dd/MM/yyyy'),
                            hour: '00:00',
                            roomId: 'all',
                            isBlocked: true,
                            hqIds: hqIds,
                        }
                    });

                    this.blockedDatesMeta = blockedDatesData.map(b => ({
                        date: format(parse(b.date, 'yyyy-MM-dd', new Date()), 'dd/MM/yyyy'),
                        hqIds: Array.isArray(b.headquarters) ? b.headquarters.map(hq => hq.id) : null,
                        reason: b.reason
                    }));
                    this.calendarEvents = [...this.calendarEvents, ...blockedDateEvents];
                })
                .catch(error => {
                    console.error('(fetchBlockedDates) error:', error);
                    // Don't show error toast for blocked dates - it's optional
                });
        },
        fetchUser() {
            UsersService.getMe()
                .then(response => {
                    this.userData = response.data;
                })
                .catch(error => {
                    console.error('(fetchUser)', error);
                    this.sendToast("Erro interno", "Algo deu errado. Tente novamente ou contate o suporte.", "error");
                    throw error;
                });
        }
    },
    created() {
        // On initial creation, check if a calendar target date is already set in the store
        const initialStoreDate = this.$store.state.calendarTargetDate;
        if (initialStoreDate) {
            try {
                this.customCalendarControlDate = parseISO(initialStoreDate);
                this.$store.dispatch('setCalendarTargetDate', { date: null });
            } catch (e) {
                console.warn('Invalid initial store calendarTargetDate:', initialStoreDate);
            }
        }
        const initialStoreHq = this.$store.state.calendarTargetHq;
        if (initialStoreHq != null) {
            try {
                this.customCalendarHqFilter = initialStoreHq;
                this.$store.dispatch('setCalendarTargetHq', { hqId: null });
            } catch (e) {
                console.warn('Invalid initial store calendarTargetHq:', initialStoreHq);
            }
        }

        // Fetch user and events (fetchBlockedDates is called from fetchEvents)
        this.fetchUser();
        this.fetchEvents();
    },
    mounted() {
        // Watch the Vuex state.calendarTargetDate directly for changes
        this.$watch(() => this.$store.state.calendarTargetDate, (newVal) => {
            if (newVal) {
                try {
                    this.customCalendarControlDate = parseISO(newVal);
                    // Clear it after applying
                    this.$store.dispatch('setCalendarTargetDate', { date: null });
                    // Refresh events for the new date
                    this.fetchEvents();
                } catch (e) {
                    console.warn('Invalid store calendarTargetDate:', newVal);
                }
            }
        });

        // Watch for calendarTargetHq changes and apply them to the local HQ filter
        this.$watch(() => this.$store.state.calendarTargetHq, (newHq) => {
            if (newHq != null) {
                try {
                    this.customCalendarHqFilter = newHq;
                    // Clear it after applying
                    this.$store.dispatch('setCalendarTargetHq', { hqId: null });
                    // Refresh events for the new HQ
                    this.fetchEvents();
                } catch (e) {
                    console.warn('Invalid store calendarTargetHq:', newHq);
                }
            }
        });
    },
    watch: {
        // TODO: Diminuir o número de requisições ao back-end 
        calendarOption() {
            this.customCalendarControlDate = new Date();
            this.customCalendarHqFilter = null;
            this.customCalendarRoomFilter = null;

            this.fetchEvents();
            this.fetchUser();
        },
        customCalendarHqFilter() {
            this.fetchEvents();
        },
        customCalendarRoomFilter() {
            this.fetchEvents();
        },
        customCalendarControlDate() {
            this.fetchEvents();
        },
        monthCalendarRange() {
            this.fetchEvents();
        }
    }
}
</script>

<style scoped></style>